import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import { createAllPhaseMenuGroups } from '../../../utils/createAllPhaseMenuGroups';
import axiosInstance from '../../../utils/axiosInstance';
import config from '../../../config.json';
import {
  Book as BookIcon,
  Person as PersonIcon,
  School as ClassIcon,
  Settings as PrepSettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Send as SendIcon,
  Home as PrepHomeIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  PlayCircle as PlayCircleIcon,
  DragIndicator as DragIndicatorIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  Grid,
  IconButton,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  Menu,
  Switch,
  FormControlLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';

export default function TaskManagement() {
  const navigate = useNavigate();
  const { courseDispatchId } = useParams();
  const [applicationTasks, setApplicationTasks] = useState([]);
  const [courseDispatch, setCourseDispatch] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [openAddSubtaskDialog, setOpenAddSubtaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [assigningTask, setAssigningTask] = useState(null);
  const [expandedUnits, setExpandedUnits] = useState({}); // 章節展開狀態
  const [targetUnitForSubtask, setTargetUnitForSubtask] = useState(null); // 新增子任務的目標章節

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('preparation', 'lesson-packages', navigate);

  // 獲取課程派發資料
  const fetchCourseDispatch = async () => {
    try {
      const response = await axiosInstance.get(`${config.getCourseDispatches}`);
      if (response.data.success) {
        const dispatch = response.data.data.find(d => d.id === parseInt(courseDispatchId));
        setCourseDispatch(dispatch);
      }
    } catch (error) {
      console.error('獲取課程派發資料錯誤:', error);
    }
  };

  // 獲取應用任務
  const fetchApplicationTasks = async () => {
    try {
      setLoading(true);
      console.log('[前端 fetchApplicationTasks] 開始獲取任務，courseDispatchId:', courseDispatchId);
      const response = await axiosInstance.get(`${config.getApplicationTasks}${courseDispatchId}`);
      console.log('[前端 fetchApplicationTasks] API 回應:', response.data);
      
      if (response.data.success) {
        const rawTasks = response.data.data || [];
        console.log('[前端 fetchApplicationTasks] 原始任務資料:', rawTasks);
        console.log('[前端 fetchApplicationTasks] 任務數量:', rawTasks.length);
        
        // 對每個任務進行 Log，檢查 customizations
        rawTasks.forEach((task, index) => {
          console.log(`[前端 fetchApplicationTasks] 任務 ${index + 1}:`, {
            id: task.id,
            title: task.title,
            unit_number: task.unit_number,
            subtask_number: task.subtask_number,
            customizations: task.customizations,
            customizations_type: typeof task.customizations,
            customizations_parsed: typeof task.customizations === 'string' ? JSON.parse(task.customizations) : task.customizations
          });
        });
        
        // 對任務進行排序：按照 unit_number 和 subtask_number 排序（與備課包相同）
        const sortedTasks = [...rawTasks].sort((a, b) => {
          // 從 customizations 或直接屬性取得章節/子任務
          let aUnit = a.unit_number;
          let bUnit = b.unit_number;
          let aSubtask = a.subtask_number;
          let bSubtask = b.subtask_number;
          
          // 如果直接屬性沒有，從 customizations 取得
          if ((aUnit === null || aUnit === undefined) && a.customizations) {
            const customizations = typeof a.customizations === 'string' ? JSON.parse(a.customizations) : a.customizations;
            aUnit = customizations?.unit_number;
            aSubtask = customizations?.subtask_number;
          }
          if ((bUnit === null || bUnit === undefined) && b.customizations) {
            const customizations = typeof b.customizations === 'string' ? JSON.parse(b.customizations) : b.customizations;
            bUnit = customizations?.unit_number;
            bSubtask = customizations?.subtask_number;
          }
          
          // 如果還是沒有，根據 order_index 自動生成
          if ((aUnit === null || aUnit === undefined) && a.order_index !== null && a.order_index !== undefined) {
            const orderIndex = parseInt(a.order_index) || 0;
            aUnit = Math.floor(orderIndex / 10000) + 1;
            aSubtask = orderIndex % 10000 || 1;
          }
          if ((bUnit === null || bUnit === undefined) && b.order_index !== null && b.order_index !== undefined) {
            const orderIndex = parseInt(b.order_index) || 0;
            bUnit = Math.floor(orderIndex / 10000) + 1;
            bSubtask = orderIndex % 10000 || 1;
          }
          
          // 預設值
          aUnit = aUnit ?? 1;
          bUnit = bUnit ?? 1;
          aSubtask = typeof aSubtask === 'number' ? aSubtask : (aSubtask ? parseInt(aSubtask) : 1);
          bSubtask = typeof bSubtask === 'number' ? bSubtask : (bSubtask ? parseInt(bSubtask) : 1);
          
          // 優先按章節號排序
          if (aUnit !== bUnit) {
            return aUnit - bUnit;
          }
          
          // 同章節內按子任務號排序（整數）
          return aSubtask - bSubtask;
        });
        
        console.log('[前端 fetchApplicationTasks] 排序後的任務:', sortedTasks.map(t => ({
          id: t.id,
          title: t.title,
          unit_number: t.unit_number,
          subtask_number: t.subtask_number
        })));
        
        setApplicationTasks(sortedTasks);
      }
    } catch (error) {
      console.error('[前端 fetchApplicationTasks] 獲取應用任務錯誤:', error);
      setSnackbar({ open: true, message: '獲取任務失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 獲取小組資料（優先使用課程派發自訂小組，若無則回退到社群預設分組）
  const fetchGroups = async () => {
    try {
      if (!courseDispatch) return;

      // 1) 先取課程派發綁定的小組（含自訂分組）
      const cdGroupsRes = await axiosInstance.get(`${config.getCourseDispatchGroups}${courseDispatch.id}/groups`);
      if (cdGroupsRes.data?.success && Array.isArray(cdGroupsRes.data.data) && cdGroupsRes.data.data.length > 0) {
        setGroups(cdGroupsRes.data.data);
        return;
      }

      // 2) 回退：取社群（班級）的預設分組
      const classGroupsRes = await axiosInstance.get(`${config.getClassGroups}${courseDispatch.class_id}`);
      if (classGroupsRes.data?.success) {
        setGroups(classGroupsRes.data.data || []);
      }
    } catch (error) {
      console.error('獲取小組資料錯誤:', error);
    }
  };

  // 新增章節 - 直接建立空任務，不需要對話框
  const handleAddUnit = async () => {
    try {
      setLoading(true);
      
      // 計算新的章節號：找到最大章節號 + 1
      let newUnitNumber = 1;
      if (applicationTasks.length > 0) {
        const maxUnit = Math.max(
          ...applicationTasks.map(t => {
            let unitNum = t.unit_number;
            if (!unitNum && t.customizations) {
              const customizations = typeof t.customizations === 'string' ? JSON.parse(t.customizations) : t.customizations;
              unitNum = customizations?.unit_number;
            }
            return unitNum || 0;
          }),
          0
        );
        newUnitNumber = maxUnit > 0 ? maxUnit + 1 : 1;
      }
      
      console.log('[前端 handleAddUnit] 新增章節，章節號:', newUnitNumber);
      
      // 直接建立新任務（使用預設標題）
      const response = await axiosInstance.post(config.createApplicationTask, {
        course_dispatch_id: courseDispatchId,
        title: `新任務`, // 預設標題
        task_content: '',
        task_type: 'idea',
        use_brainstorm_wall: false,
        is_optional: false,
        unit_number: newUnitNumber,
        subtask_number: 1
      });
      
      if (response.data.success) {
        const newTask = response.data.task || response.data.data;
        // 自動打開編輯對話框
        if (newTask) {
          setEditingTask(newTask);
          setOpenTaskDialog(true);
        }
        setSnackbar({ open: true, message: '章節已建立，請編輯任務內容', severity: 'success' });
        fetchApplicationTasks();
      } else {
        alert('建立章節失敗');
      }
    } catch (error) {
      console.error('建立章節錯誤:', error);
      alert(error.response?.data?.message || '建立章節失敗');
    } finally {
      setLoading(false);
    }
  };

  // 新增子任務
  const handleAddSubtask = (targetUnitNumber = null) => {
    setTargetUnitForSubtask(targetUnitNumber);
    setEditingTask({
      id: null,
      type: 'subtask',
      targetUnitNumber: targetUnitNumber,
      title: '',
      task_content: '',
      task_type: 'idea',
      use_brainstorm_wall: false,
      is_optional: false
    });
    setOpenAddSubtaskDialog(true);
  };

  // 編輯任務
  const handleEditTask = (task) => {
    setEditingTask(task);
    setOpenTaskDialog(true);
  };

  // 刪除任務
  const handleDeleteTask = async (taskId) => {
    if (window.confirm('確定要刪除這個任務嗎？此操作無法復原！')) {
      try {
        setLoading(true);
        const response = await axiosInstance.delete(`${config.deleteApplicationTask}${taskId}`);
        if (response.data.success) {
          setSnackbar({ open: true, message: '任務已刪除', severity: 'success' });
          fetchApplicationTasks();
        }
      } catch (error) {
        console.error('刪除任務錯誤:', error);
        setSnackbar({ open: true, message: '刪除任務失敗', severity: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  // 調整任務順序（與備課包相同邏輯：只交換兩個任務的 unit_number 和 subtask_number）
  const handleMoveTask = async (taskId, direction) => {
    try {
      setLoading(true);
      
      // 按照 unit_number 和 subtask_number 排序任務列表（用於確定移動位置）
      const sortedTasks = [...applicationTasks].sort((a, b) => {
        let aUnit = a.unit_number;
        let bUnit = b.unit_number;
        let aSubtask = a.subtask_number;
        let bSubtask = b.subtask_number;
        
        // 如果直接屬性沒有，從 customizations 取得
        if (!aUnit && a.customizations) {
          const customizations = typeof a.customizations === 'string' ? JSON.parse(a.customizations) : a.customizations;
          aUnit = customizations?.unit_number;
          aSubtask = customizations?.subtask_number;
        }
        if (!bUnit && b.customizations) {
          const customizations = typeof b.customizations === 'string' ? JSON.parse(b.customizations) : b.customizations;
          bUnit = customizations?.unit_number;
          bSubtask = customizations?.subtask_number;
        }
        
        // 預設值
        aUnit = aUnit ?? 1;
        bUnit = bUnit ?? 1;
        aSubtask = typeof aSubtask === 'number' ? aSubtask : (aSubtask ? parseInt(aSubtask) : 0);
        bSubtask = typeof bSubtask === 'number' ? bSubtask : (bSubtask ? parseInt(bSubtask) : 0);
        
        if (aUnit !== bUnit) {
          return aUnit - bUnit;
        }
        return aSubtask - bSubtask;
      });
      
      const taskIndex = sortedTasks.findIndex(task => task.id === taskId);
      
      if (taskIndex === -1) {
        alert('找不到要移動的任務');
        return;
      }

      const newIndex = direction === 'up' ? taskIndex - 1 : taskIndex + 1;
      
      if (newIndex < 0 || newIndex >= sortedTasks.length) {
        return; // 已經到達邊界
      }

      // 只交換兩個任務的 unit_number 和 subtask_number
      const task1 = sortedTasks[taskIndex];
      const task2 = sortedTasks[newIndex];

      // 調用後端API交換順序（後端會交換兩個任務的 unit_number 和 subtask_number）
      const response = await axiosInstance.put(
        `${config.reorderApplicationTasks}${courseDispatchId}/reorder`,
        { 
          task_orders: [
            { task_id: task1.id, order_index: 0 },
            { task_id: task2.id, order_index: 1 }
          ]
        }
      );

      if (response.data.success) {
        setSnackbar({ open: true, message: '任務順序已調整', severity: 'success' });
        fetchApplicationTasks();
      }
    } catch (error) {
      console.error('調整任務順序錯誤:', error);
      setSnackbar({ open: true, message: '調整順序失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 初始化載入
  useEffect(() => {
    if (courseDispatchId) {
      fetchCourseDispatch();
      fetchApplicationTasks();
    }
  }, [courseDispatchId]);

  useEffect(() => {
    if (courseDispatch) {
      fetchGroups();
    }
  }, [courseDispatch]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'default';
      case 'assigned': return 'primary';
      case 'in-progress': return 'warning';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return '草稿';
      case 'assigned': return '已指派';
      case 'in-progress': return '進行中';
      case 'completed': return '已完成';
      default: return '未知';
    }
  };

  const handleAssignTask = (task) => {
    setAssigningTask(task);
    setOpenAssignDialog(true);
  };

  // 按章節分組任務
  const groupTasksByUnit = () => {
    const grouped = {};
    
    applicationTasks.forEach(task => {
      // 從 customizations 或直接屬性取得章節/子任務
      let unitNumber = task.unit_number;
      let subtaskNumber = task.subtask_number;
      
      // 如果直接屬性沒有，從 customizations 取得
      if ((unitNumber === null || unitNumber === undefined) && task.customizations) {
        const customizations = typeof task.customizations === 'string' ? JSON.parse(task.customizations) : task.customizations;
        unitNumber = customizations?.unit_number;
        subtaskNumber = customizations?.subtask_number;
      }
      
      // 如果還是沒有，根據 order_index 自動生成
      if ((unitNumber === null || unitNumber === undefined) && task.order_index !== null && task.order_index !== undefined) {
        const orderIndex = parseInt(task.order_index) || 0;
        unitNumber = Math.floor(orderIndex / 10000) + 1;
        subtaskNumber = orderIndex % 10000 || 1;
      }
      
      // 預設值
      unitNumber = unitNumber ?? 1;
      subtaskNumber = typeof subtaskNumber === 'number' ? subtaskNumber : (subtaskNumber ? parseInt(subtaskNumber) : 1);
      
      const unitKey = `unit_${unitNumber}`;
      
      if (!grouped[unitKey]) {
        grouped[unitKey] = {
          unitNumber: unitNumber,
          tasks: []
        };
      }
      
      grouped[unitKey].tasks.push({
        ...task,
        displayUnitNumber: unitNumber,
        displaySubtaskNumber: subtaskNumber
      });
    });
    
    // 對每個章節的任務按 subtask_number 排序
    Object.keys(grouped).forEach(key => {
      grouped[key].tasks.sort((a, b) => {
        const aNum = typeof a.displaySubtaskNumber === 'number' ? a.displaySubtaskNumber : (parseInt(a.displaySubtaskNumber) || 0);
        const bNum = typeof b.displaySubtaskNumber === 'number' ? b.displaySubtaskNumber : (parseInt(b.displaySubtaskNumber) || 0);
        return aNum - bNum;
      });
    });
    
    return grouped;
  };

  const toggleUnit = (unitKey) => {
    setExpandedUnits(prev => ({
      ...prev,
      [unitKey]: !prev[unitKey]
    }));
  };

  const grouped = groupTasksByUnit();
  const unitKeys = Object.keys(grouped).sort((a, b) => {
    return grouped[a].unitNumber - grouped[b].unitNumber;
  });

  return (
    <div className="teacher-layout task-management-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="preparation"
          onPhaseChange={(phase) => {
            if (phase === 'preparation') {
              navigate('/teacher/preparation');
            } else if (phase === 'implementation') {
              navigate('/teacher/implementation');
            } else if (phase === 'diagnosis') {
              navigate('/teacher/diagnosis');
            }
          }}
        />
        
        <div className="class-header-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/teacher/preparation/course-dispatch')}
              className="btn-secondary"
            >
              返回
            </Button>
            <h1 className="main-title">
              {courseDispatch?.title || '任務管理'}
            </h1>
          </div>
        </div>

        {/* 課程資訊卡片 */}
        {courseDispatch && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>課程資訊</Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>社群：</strong>{courseDispatch.Class?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>備課包：</strong>{courseDispatch.LessonPackage?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>學科/年級：</strong>{courseDispatch.LessonPackage?.subject} / {courseDispatch.LessonPackage?.grade}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>統計資訊</Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>總任務數：</strong>{applicationTasks.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>已完成：</strong>{applicationTasks.filter(t => t.status === 'completed').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>進行中：</strong>{applicationTasks.filter(t => t.status === 'in-progress').length}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* 操作按鈕 */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            className="btn-primary"
            startIcon={<AddIcon />}
            onClick={handleAddUnit}
            disabled={loading}
            sx={{
              px: 2.5,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(43,104,127,0.3)'
            }}
          >
            新增章節
          </Button>
        </Box>

        {/* 任務列表 - 按章節分組顯示 */}
        {applicationTasks.length > 0 ? (
          <List>
            {unitKeys.length > 0 && unitKeys.map(unitKey => {
              const unitData = grouped[unitKey];
              const isExpanded = expandedUnits[unitKey] !== false; // 預設展開
              
              return (
                <Accordion
                  key={unitKey}
                  expanded={isExpanded}
                  onChange={() => toggleUnit(unitKey)}
                  sx={{
                    mb: 1.5,
                    '&:before': { display: 'none' },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: '#e3f2fd',
                      minHeight: 48,
                      '&.Mui-expanded': {
                        minHeight: 48
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        章節 {unitData.unitNumber} ({unitData.tasks.length} 個任務)
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSubtask(unitData.unitNumber);
                        }}
                        sx={{
                          color: '#1976d2',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.1)'
                          }
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, bgcolor: '#fafafa' }}>
                    {unitData.tasks.map((task, index) => {
                      // 計算該任務在所有任務中的索引（用於判斷是否可以移動）
                      const sortedAllTasks = [...applicationTasks].sort((a, b) => {
                        let aUnit = a.unit_number;
                        let bUnit = b.unit_number;
                        let aSubtask = a.subtask_number;
                        let bSubtask = b.subtask_number;
                        
                        if (!aUnit && a.customizations) {
                          const customizations = typeof a.customizations === 'string' ? JSON.parse(a.customizations) : a.customizations;
                          aUnit = customizations?.unit_number;
                          aSubtask = customizations?.subtask_number;
                        }
                        if (!bUnit && b.customizations) {
                          const customizations = typeof b.customizations === 'string' ? JSON.parse(b.customizations) : b.customizations;
                          bUnit = customizations?.unit_number;
                          bSubtask = customizations?.subtask_number;
                        }
                        
                        aUnit = aUnit ?? 1;
                        bUnit = bUnit ?? 1;
                        aSubtask = typeof aSubtask === 'number' ? aSubtask : (aSubtask ? parseInt(aSubtask) : 0);
                        bSubtask = typeof bSubtask === 'number' ? bSubtask : (bSubtask ? parseInt(bSubtask) : 0);
                        
                        if (aUnit !== bUnit) {
                          return aUnit - bUnit;
                        }
                        return aSubtask - bSubtask;
                      });
                      const globalTaskIndex = sortedAllTasks.findIndex(t => t.id === task.id);
                      const canMoveUp = globalTaskIndex > 0;
                      const canMoveDown = globalTaskIndex < sortedAllTasks.length - 1;
                      
                      return (
                        <ListItem
                          key={task.id}
                          sx={{
                            mb: 1,
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: '#e0e0e0',
                            backgroundColor: 'white',
                            transition: 'all 0.3s',
                            ml: 1,
                            mr: 1,
                            mt: index === 0 ? 1 : 0,
                            mb: index === unitData.tasks.length - 1 ? 1 : 1,
                            '&:hover': {
                              borderColor: '#2b687f',
                              boxShadow: '0 4px 12px rgba(43,104,127,0.15)',
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <ListItemIcon>
                        <Chip 
                              label={`${task.displayUnitNumber}-${task.displaySubtaskNumber}`}
                          size="small" 
                              sx={{
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                fontSize: '0.7rem',
                                height: 20,
                                fontWeight: 500
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {task.title || '未命名任務'}
                                </Typography>
                                {task.is_customized && (
                                  <Chip label="已修改" size="small" color="warning" variant="outlined" />
                                )}
                                <Chip label={task.task_type || '一般任務'} size="small" variant="outlined" />
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  {task.use_brainstorm_wall && (
                      <Chip 
                                      label="問題牆" 
                        size="small" 
                                      color="success" 
                                      sx={{ fontWeight: 600 }}
                                    />
                      )}
                                  {task.is_optional === false && (
                      <Chip 
                                      label="需繳交" 
                        size="small" 
                                      color="primary" 
                                      sx={{ fontWeight: 600 }}
                      />
                                  )}
                                </Box>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                      {task.assigned_groups && task.assigned_groups.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {task.assigned_groups.map((groupId, idx) => {
                            const group = groups.find(g => g.id === groupId);
                            return group ? (
                                        <Chip key={idx} label={group.groupName} size="small" variant="outlined" />
                            ) : null;
                          })}
                        </Box>
                      ) : (
                                  <Typography variant="caption" color="text.secondary">
                          未指派
                        </Typography>
                      )}
                              </Box>
                            }
                          />
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Tooltip title={canMoveUp ? '上移' : ''}>
                            <span>
                              <IconButton
                                size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMoveTask(task.id, 'up');
                                    }}
                                    disabled={!canMoveUp}
                                sx={{ p: 0.25 }}
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                              <Tooltip title={canMoveDown ? '下移' : ''}>
                            <span>
                              <IconButton
                                size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMoveTask(task.id, 'down');
                                    }}
                                    disabled={!canMoveDown}
                                sx={{ p: 0.25 }}
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                        <Tooltip title="編輯任務">
                          <IconButton
                            size="small"
                            onClick={() => handleEditTask(task)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="任務管理">
                          <IconButton
                            size="small"
                            onClick={() => handleAssignTask(task)}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="刪除任務">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTask(task.id)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                        </ListItem>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </List>
        ) : (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <AssignmentIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                尚未建立任務
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                點擊上方「新增任務」按鈕來建立第一個任務
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* 新增章節對話框 */}
        <TaskEditDialog
          open={openTaskDialog}
          task={editingTask}
          courseDispatchId={courseDispatchId}
          applicationTasks={applicationTasks}
          dialogType="unit"
          onClose={() => {
            setOpenTaskDialog(false);
            setEditingTask(null);
          }}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            fetchApplicationTasks();
            setOpenTaskDialog(false);
            setEditingTask(null);
          }}
        />

        {/* 新增子任務對話框 */}
        <TaskEditDialog
          open={openAddSubtaskDialog}
          task={editingTask}
          courseDispatchId={courseDispatchId}
          applicationTasks={applicationTasks}
          dialogType="subtask"
          targetUnitNumber={targetUnitForSubtask}
          onClose={() => {
            setOpenAddSubtaskDialog(false);
            setEditingTask(null);
            setTargetUnitForSubtask(null);
          }}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            fetchApplicationTasks();
            setOpenAddSubtaskDialog(false);
            setEditingTask(null);
            setTargetUnitForSubtask(null);
          }}
        />

        {/* 任務指派對話框 */}
        <TaskAssignDialog
          open={openAssignDialog}
          task={assigningTask}
          groups={groups}
          onClose={() => {
            setOpenAssignDialog(false);
            setAssigningTask(null);
          }}
          onAssign={(assignedGroups) => {
            setSnackbar({ open: true, message: '任務指派成功', severity: 'success' });
            fetchApplicationTasks();
            setOpenAssignDialog(false);
            setAssigningTask(null);
          }}
        />

        {/* 通知訊息 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

// 任務編輯對話框組件
function TaskEditDialog({ open, task, courseDispatchId, onClose, onSuccess, applicationTasks = [], dialogType = 'edit', targetUnitNumber = null }) {
  const [formData, setFormData] = useState({
    title: '',
    task_content: '',
    task_type: 'idea',
    use_brainstorm_wall: false,
    is_optional: false,
    unit_number: null,
    subtask_number: null
  });

  useEffect(() => {
    if (task) {
      // 從 customizations 或直接屬性取得章節/子任務
      let unitNumber = task.unit_number;
      let subtaskNumber = task.subtask_number;
      
      if (!unitNumber && task.customizations) {
        const customizations = typeof task.customizations === 'string' ? JSON.parse(task.customizations) : task.customizations;
        unitNumber = customizations?.unit_number;
        subtaskNumber = customizations?.subtask_number;
      }
      
      setFormData({
        title: task.title || '',
        task_content: task.task_content || '',
        task_type: task.task_type || 'idea',
        use_brainstorm_wall: task.use_brainstorm_wall ?? false,
        is_optional: task.is_optional ?? false,
        unit_number: unitNumber ?? null,
        subtask_number: subtaskNumber ?? null
      });
    } else {
      // 重置為預設值（新增模式）
      // 根據 dialogType 計算新的章節/子任務編號
      const existingTasks = applicationTasks || [];
      const taskType = dialogType; // 'unit' 或 'subtask'
      const targetUnit = targetUnitNumber !== null ? targetUnitNumber : (task?.targetUnitNumber || null);
      
      let newUnitNumber = 1;
      let newSubtaskNumber = 1;
      
      if (existingTasks.length > 0) {
        if (taskType === 'unit') {
          // 新增章節：找到最大章節號 + 1
          const maxUnit = Math.max(
            ...existingTasks.map(t => {
              let unitNum = t.unit_number;
              if (!unitNum && t.customizations) {
                const customizations = typeof t.customizations === 'string' ? JSON.parse(t.customizations) : t.customizations;
                unitNum = customizations?.unit_number;
              }
              return unitNum || 0;
            }),
            0
          );
          newUnitNumber = maxUnit > 0 ? maxUnit + 1 : 1;
          newSubtaskNumber = 1; // 新章節的第一個子任務
        } else {
          // 新增子任務
          if (targetUnit !== null) {
            // 指定章節號
            newUnitNumber = targetUnit;
            const sameUnitTasks = existingTasks.filter(t => {
              let unitNum = t.unit_number;
              if (!unitNum && t.customizations) {
                const customizations = typeof t.customizations === 'string' ? JSON.parse(t.customizations) : t.customizations;
                unitNum = customizations?.unit_number;
              }
              return unitNum === targetUnit;
            });
            
            if (sameUnitTasks.length > 0) {
              const maxSubtask = Math.max(
                ...sameUnitTasks.map(t => {
                  let subtaskNum = t.subtask_number;
                  if (!subtaskNum && t.customizations) {
                    const customizations = typeof t.customizations === 'string' ? JSON.parse(t.customizations) : t.customizations;
                    subtaskNum = customizations?.subtask_number;
                  }
                  const num = typeof subtaskNum === 'number' ? subtaskNum : (subtaskNum ? parseInt(subtaskNum) : 0);
                  return num;
                }),
                0
              );
              newSubtaskNumber = maxSubtask + 1;
            } else {
              newSubtaskNumber = 1;
            }
          } else {
            // 使用最後一個章節
            const maxUnit = Math.max(
              ...existingTasks.map(t => {
                let unitNum = t.unit_number;
                if (!unitNum && t.customizations) {
                  const customizations = typeof t.customizations === 'string' ? JSON.parse(t.customizations) : t.customizations;
                  unitNum = customizations?.unit_number;
                }
                return unitNum || 0;
              }),
              0
            );
            
            if (maxUnit > 0) {
              newUnitNumber = maxUnit;
              const sameUnitTasks = existingTasks.filter(t => {
                let unitNum = t.unit_number;
                if (!unitNum && t.customizations) {
                  const customizations = typeof t.customizations === 'string' ? JSON.parse(t.customizations) : t.customizations;
                  unitNum = customizations?.unit_number;
                }
                return unitNum === maxUnit;
              });
              
              if (sameUnitTasks.length > 0) {
                const maxSubtask = Math.max(
                  ...sameUnitTasks.map(t => {
                    let subtaskNum = t.subtask_number;
                    if (!subtaskNum && t.customizations) {
                      const customizations = typeof t.customizations === 'string' ? JSON.parse(t.customizations) : t.customizations;
                      subtaskNum = customizations?.subtask_number;
                    }
                    const num = typeof subtaskNum === 'number' ? subtaskNum : (subtaskNum ? parseInt(subtaskNum) : 0);
                    return num;
                  }),
                  0
                );
                newSubtaskNumber = maxSubtask + 1;
              } else {
                newSubtaskNumber = 1;
              }
            } else {
              newUnitNumber = 1;
              newSubtaskNumber = 1;
            }
          }
        }
      }
      
      setFormData({
        title: '',
        task_content: '',
        task_type: 'idea',
        use_brainstorm_wall: false,
        is_optional: false,
        unit_number: newUnitNumber,
        subtask_number: newSubtaskNumber
      });
    }
  }, [task, open, applicationTasks]);

  // 用於防止重複點擊的 ref
  const isSubmittingRef = React.useRef(false);

  const handleSubmit = async () => {
    if (!formData.title) {
      alert('請輸入任務標題');
      return;
    }

    // 防重複點擊：如果正在提交，直接返回
    if (isSubmittingRef.current) {
      console.log('[handleSubmit] 正在提交中，忽略重複點擊');
      return;
    }

    try {
      isSubmittingRef.current = true;
      if (task?.id) {
        // 編輯現有任務 - 不允許修改章節/子任務編號
        const response = await axiosInstance.put(`${config.updateApplicationTask}${task.id}`, {
          title: formData.title,
          task_content: formData.task_content,
          task_type: formData.task_type,
          use_brainstorm_wall: formData.use_brainstorm_wall,
          is_optional: formData.is_optional
          // 不傳遞 unit_number 和 subtask_number，保持原有值
        });
        if (response.data.success) {
          onSuccess('任務已更新');
        } else {
          alert('更新任務失敗');
        }
      } else {
        // 建立新任務 - 傳遞 unit_number 和 subtask_number 給後端
        const response = await axiosInstance.post(config.createApplicationTask, {
          course_dispatch_id: courseDispatchId,
          title: formData.title,
          task_content: formData.task_content,
          task_type: formData.task_type,
          use_brainstorm_wall: formData.use_brainstorm_wall,
          is_optional: formData.is_optional,
          unit_number: formData.unit_number,
          subtask_number: formData.subtask_number
        });
        if (response.data.success) {
          // 新增任務後，後端會自動重新計算所有任務的章節/子任務編號
          onSuccess('任務已建立，章節/子任務編號已自動生成');
        } else {
          alert('建立任務失敗');
        }
      }
    } catch (error) {
      console.error('儲存任務錯誤:', error);
      alert(error.response?.data?.message || '儲存任務失敗');
    } finally {
      // 延遲清除標記，避免快速重複點擊
      setTimeout(() => {
        isSubmittingRef.current = false;
      }, 1000);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {task?.id ? '編輯任務' : (dialogType === 'unit' ? '新增章節' : '新增子任務')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
          <TextField
            label="任務標題"
            value={formData.title}
            onChange={(e) => {
              const value = e.target.value || '';
              if (value.length <= 15) {
                setFormData({ ...formData, title: value });
              }
            }}
            fullWidth
            required
            placeholder="請輸入任務標題"
            helperText={`${Math.min((formData.title || '').length, 15)}/15 字`}
            inputProps={{ maxLength: 15 }}
          />

          <FormControl fullWidth>
            <InputLabel>任務類型</InputLabel>
            <Select
              value={formData.task_type}
              onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}
              label="任務類型"
            >
              <MenuItem value="idea">想法</MenuItem>
              <MenuItem value="question">提問</MenuItem>
              <MenuItem value="info">資訊</MenuItem>
              <MenuItem value="experiment">實驗</MenuItem>
              <MenuItem value="record">紀錄</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="任務內容"
            value={formData.task_content}
            onChange={(e) => setFormData({ ...formData, task_content: e.target.value })}
            multiline
            rows={3}
            fullWidth
            placeholder="請輸入任務的詳細內容或說明"
          />

          <Box sx={{ 
            p: 2, 
            border: '1px solid #e0e0e0', 
            borderRadius: 1, 
            bgcolor: '#fafafa',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              任務設定
            </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={!formData.is_optional}
                onChange={(e) => setFormData({ ...formData, is_optional: !e.target.checked })}
                  color="primary"
              />
            }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    需要繳交成果
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.is_optional === false ? '學生必須繳交任務成果' : '學生不須繳交任務成果'}
                  </Typography>
                </Box>
              }
              sx={{ m: 0 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.use_brainstorm_wall}
                onChange={(e) => setFormData({ ...formData, use_brainstorm_wall: e.target.checked })}
                  color="success"
              />
            }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    啟用問題牆
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.use_brainstorm_wall ? '使用問題牆引導學生提問' : '不使用問題牆'}
                  </Typography>
                </Box>
              }
              sx={{ m: 0 }}
            />
          </Box>

          {/* 章節/子任務編號自動生成，不允許手動編輯 */}
          {formData.unit_number && formData.subtask_number && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                章節/子任務編號：<strong>{formData.unit_number}-{formData.subtask_number}</strong>
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose}>
          取消
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          {task?.id ? '更新' : '建立'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 任務指派對話框組件
function TaskAssignDialog({ open, task, groups, onClose, onAssign }) {
  const [selectedGroups, setSelectedGroups] = useState([]);

  useEffect(() => {
    if (task) {
      setSelectedGroups(task.assigned_groups || []);
    }
  }, [task]);

  const handleGroupToggle = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleSelectAll = () => {
    if (selectedGroups.length === groups.length) {
      // 如果已全選，則取消全選
      setSelectedGroups([]);
    } else {
      // 否則全選所有小組
      setSelectedGroups(groups.map(g => g.id));
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await axiosInstance.put(`${config.assignApplicationTask}${task.id}/assign`, {
        assigned_groups: selectedGroups
      });
      if (response.data.success) {
        onAssign(selectedGroups);
      }
    } catch (error) {
      console.error('指派任務錯誤:', error);
      alert('指派任務失敗');
    }
  };

  const allSelected = groups.length > 0 && selectedGroups.length === groups.length;
  const someSelected = selectedGroups.length > 0 && selectedGroups.length < groups.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon sx={{ color: '#2b687f' }} />
          <Typography variant="h6">排程任務</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
          <Box sx={{ 
            p: 2, 
            bgcolor: '#f0f8ff', 
            borderRadius: 2,
            border: '1px solid #2b687f'
          }}>
            <Typography variant="subtitle1" fontWeight="600" color="#2b687f" gutterBottom>
              {task?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {task?.task_content || '暫無內容'}
            </Typography>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="600">
              選擇指派的小組：
            </Typography>
              {groups.length > 0 && (
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  sx={{ 
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.875rem',
                    textTransform: 'none'
                  }}
                >
                  {allSelected ? '取消全選' : '全選'}
                </Button>
              )}
            </Box>
            <Box sx={{ 
              maxHeight: 300, 
              overflow: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 1
            }}>
              {groups.length > 0 ? (
                <>
                  {/* 全選選項 */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={handleSelectAll}
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight="600">
                        全選所有小組 ({groups.length} 組)
                      </Typography>
                    }
                    sx={{ 
                      display: 'flex',
                      p: 1,
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: allSelected || someSelected ? '#f0f8ff' : 'transparent',
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                  />
                  <Divider sx={{ my: 0.5 }} />
                  {/* 個別小組選項 */}
                  {groups.map((group) => (
                  <FormControlLabel
                    key={group.id}
                    control={
                      <Checkbox
                        checked={selectedGroups.includes(group.id)}
                        onChange={() => handleGroupToggle(group.id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{group.groupName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {group.GroupMembers?.length || 0} 人
                        </Typography>
                      </Box>
                    }
                    sx={{ 
                      display: 'flex',
                      p: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                  />
                  ))}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  尚未建立分組
                </Typography>
              )}
            </Box>
          </Box>

          {selectedGroups.length > 0 && (
            <Alert severity="info">
              已選擇 {selectedGroups.length} 個小組，任務將排程給這些小組
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose}>
          取消
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={selectedGroups.length === 0}
        >
          排程
        </Button>
      </DialogActions>
    </Dialog>
  );
}
